<template>
    <header
        v-bind="$attrs"
        ref="hero"
        class="restaurant-hero"
    >
        <div
            ref="content"
            class="restaurant-hero__content"
        >
            <template
                v-if="pending"
            >
                <Skeletor
                    v-for="i in [1,2,3]"
                    :key="i"
                    class="restaurant-hero__image"
                    width="460"
                    height="100%"
                    :shimmer="false"
                />
            </template>

            <div class="restaurant-hero__body">
                <BaseImage
                    v-if="logo"
                    :image="logo"
                    :width="logo.width"
                    :height="logo.height"
                    :fade-in-view="false"
                    class="restaurant-hero__logo"
                />

                <div class="restaurant-hero__description">
                    {{ description }}
                </div>

                <div class="restaurant-hero__button-wrapper">
                    <BaseButton
                        v-if="!pending && restaurantId && tableBookingProvider === 'zenchef'"
                        class="restaurant-hero__cta"
                        element="a"
                        href="#zc-action-open"
                        :color="backgroundColor"
                        :text-color="textColor"
                        :style="{
                            animationDelay: animationDelay
                        }"
                    >
                        {{ callToActionText }}
                    </BaseButton>

                    <BaseButton
                        v-else-if="!pending && tableBookingProvider === 'opentable' && opentableReservationLink"
                        class="restaurant-hero__cta"
                        element="a"
                        target="_blank"
                        :href="opentableReservationLink"
                        :color="backgroundColor"
                        :text-color="textColor"
                        :style="{
                            animationDelay: animationDelay
                        }"
                    >
                        {{ callToActionText }}
                    </BaseButton>

                    <BaseArrowLink
                        class="restaurant-hero__cta"
                        :link="secondaryButtonLink"
                    >
                        {{ secondaryButtonLink?.text }}
                    </BaseArrowLink>
                </div>
            </div>


            <div
                class="restaurant-hero__images"
            >
                <BaseImage
                    v-for="(image, index) in images"
                    ref="imageElements"
                    :key="index"
                    class="restaurant-hero__image"
                    :image="image"
                    :sizes="getImageSizes(index)"
                    loading="eager"
                    fetchpriority="high"
                    :fade-in-view="true"
                />
            </div>
        </div>

        <WidgetZenchef
            v-if="tableBookingProvider === 'zenchef' && restaurantId"
            :restaurant-id="restaurantId"
            :theme-color="backgroundColor"
        />
    </header>
</template>

<script setup lang="ts">
interface ImageObject {
    id: string;
    url?: string;
    alt?: string;
    title?: string;
    width: string;
    height: string;
    [key: string]: string | undefined;
}

interface Props {
    backgroundColor?: String;
    textColor?: String;
    images?: ImageObject[];
    logo?: ImageObject;
    pending?: boolean;
    loadingStateShown?: boolean;
    description?: String;
    callToActionText?: String;
    secondaryButtonLink?: Object;
    restaurantId?: string;
    tableBookingProvider?: string;
    opentableReservationLink?: string;
}

const props = withDefaults(defineProps<Props>(), {
    backgroundColor: () => '#6c895d',
    textColor: () => '#ffffff',
    images: undefined,
    logo: undefined,
    loadingStateShown: false,
    description: undefined,
    callToActionText: undefined,
    restaurantId: undefined,
    tableBookingProvider: undefined,
    secondaryButtonLink: undefined,
    opentableReservationLink: undefined
});

const backgroundColor = computed(() => props.backgroundColor);
const textColor = computed(() => props.textColor);

/** Set a global accent color, so it may be re-used in for example the header bar background */
useHead({
    style: () => [
        `:root {
            --accent-color: ${backgroundColor?.value};
            --accent-text-color: ${textColor?.value};
            --navigation-background: ${backgroundColor?.value};
            --navigation-text-color: var(--color-darkest);
        }`
    ]
});

function getImageSizes(index: Number) {
    const desktopWidth = 418;
    const mobileWidth = 113;

    return `(min-width: 1440px) ${desktopWidth}px, (min-width: 768px) ${mobileWidth*2}px, ${mobileWidth}px`;
}

const CraftLink = resolveComponent('CraftLink');

/** Animations  */
const hero = ref();
const imageElements = ref([]);
const content = ref();
const cta = ref();

// Set a scrollPercentage property as a CSS variable on the body element
const setPercentage = ref(0);

const animationDelay = computed(() => {
    return (setPercentage.value * -1).toString() + 's';
});

// eslint-disable-next-line no-var, vars-on-top
declare var loadingAnimationEnded: Boolean;

const parallaxApplied = ref(false);

function applyParallax() {
    if (parallaxApplied.value || !hero.value || !content.value || !cta.value) {
        return;
    }

    const { parallax } = useScrollAnimations();

    parallax(content.value, {
        trigger: hero.value,
        direction: 'down'
    });

    parallax(cta.value, {
        trigger: hero.value,
        offset: .8
    });

    parallaxApplied.value = true;
}

onMounted(() => {
    if (typeof loadingAnimationEnded === 'undefined' || loadingAnimationEnded === true) {
        applyParallax();
    } else if (loadingAnimationEnded === false) {
        document.querySelector('.restaurant-hero')?.addEventListener('animationend', () => {
            applyParallax();
        });

        if (props.loadingStateShown === true) {
            applyParallax();
        }
    }
});

onBeforeUnmount(() => {
    document.querySelector('.restaurant-hero')?.removeEventListener('animationend', () => {
        applyParallax();
    });
});
</script>

<style lang="less">
.restaurant-hero {
    position: relative;
    z-index: 1;

    height: 79vh;
    min-height: 500px;
    overflow: hidden;

    background-color: var(--accent-color);
    color: var(--accent-text-color);
    transform: translateY(0);
    top: calc(var(--banner-height) * -1);

    @media @q-md-min {
        min-height: 750px;
    }
}

.restaurant-hero__content {
    @media @q-md-max {
        display: flex;
        flex-direction: column;
        height: 100%;

        .restaurant-hero__images {
            flex: 0 1 auto;
            min-height: 0;
        }

        .restaurant-hero__body {
            flex: 1 0 auto;
            order: 2;
            padding-inline: var(--grid-gutter-margin);
        }
    }
    @media @q-md-min {
        .base-grid(var(--grid-maxWidth-page), 4);
        transform: translateZ(0); //prevents rounding layout shift on hydrate
    }
}

.restaurant-hero__body {
    grid-column: 2/right-side-gutter;
    grid-row: 3/-1;

    display: flex;
    justify-content: flex-end;
    flex-direction: column;
    gap: var(--spacing-md);
    align-items: flex-start;

    padding-block: var(--grid-gutter-margin);
    line-height: var(--lineHeight-md);
    z-index: 99;

    @media @q-md-min {
        grid-column: second;
        grid-row: 2/4;
        justify-content: center;
        max-width: 20rem;
        margin-inline: auto;
    }
}

.restaurant-hero__images {
    .base-grid(100%, 4);
    width: 100%;

    grid-column: 1/-1;
    grid-row: 1/5;


    @media @q-md-max {
        .base-grid(100%, 9);
        grid-row: 1/3;
        margin-top: var(--navbar-height);
    }

    @media @q-sm-max {
        .base-grid(100%, 12);
        margin-top: var(--navbar-height);
    }
}

.restaurant-hero__image {
    display: flex;
    overflow: hidden;
    align-self: start;
    height: 100%;
    max-height: 100%;

    &:nth-child(1) {
        grid-column: 1/right-gutter;
        grid-row: 1/-1;

        @media @q-md-min {
            grid-column: left-side-gutter/left-gutter;
            grid-row: 2/-1;
            align-self: end;
        }
    }

    &:nth-child(2) {
        grid-column: third/-1;
        grid-row: 1/4;

        @media @q-md-min {
            grid-row: 1/3;
            align-self: end;
        }
    }

    &:nth-child(3) {
        grid-column: third/-1;
        grid-row: 5/8;

        @media @q-md-min {
            grid-row: 3/-1;
            align-self: end;
            padding-top: var(--grid-gutter-margin);
        }
    }

    picture {
        overflow: hidden;
        width: 100%;
        height: 100%;
    }

    img {
        height: 100%;
        max-height: 100%;
        object-fit: cover;
        object-position: center center;
    }
}

.restaurant-hero__logo {
    flex: 0 0 auto;
}

.restaurant-hero__button-wrapper {
    display: flex;
    gap: var(--spacing-md);
    white-space: nowrap;
}

.dn-button.restaurant-hero__cta {
    flex: 0 0 3.125rem;
    height: 3rem;
    margin: 0;
    width: initial;

    @media @q-md-max {
        align-self: stretch;
        justify-content: center;
    }
}
</style>
